/**
 * Query-only hook for notifications data with infinite scroll
 *
 * Wraps useSuspenseInfiniteQuery with cursor-based pagination and exposes an
 * optional `select` parameter so consumers can derive stable slices of
 * the data via React Query's built-in structural sharing.
 *
 * Because this hook suspends, callers must be wrapped in a QueryBoundary
 * (or equivalent Suspense + ErrorBoundary). Feature-flag gating should
 * happen *above* the component that calls this hook, not via an `enabled`
 * option.
 */

import { useSuspenseInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import { useEntity } from '../../../contexts/EntityContext';
import { fetchNotifications } from '../api';
import type {
  NotificationsApiResponse,
  FetchNotificationsParams,
  FetchUnreadCountParams,
} from '../types';
import { QUERY_STALE_TIME_MS, POLLING_INTERVAL_MS } from '../constants';

/**
 * Query key factory for notifications
 * Ensures consistent cache keys across the application
 *
 * Uses specific, non-hierarchical keys to prevent accidental cache invalidation
 * and enable precise cache targeting for mutations.
 */
export const notificationsKeys = {
  /**
   * Full query key for infinite list with exact params matching.
   * Use for getQueryData/setQueryData operations that need exact cache hits.
   */
  infiniteList: (entityId: string, params: FetchNotificationsParams) =>
    ['notificationsInfiniteList', entityId, params] as const,

  /**
   * Partial query key for prefix matching across all infinite list queries for an entity.
   * Use for invalidateQueries/cancelQueries operations that should affect all param variations.
   */
  infiniteListByEntity: (entityId: string) => ['notificationsInfiniteList', entityId] as const,

  /** Dedicated key for the lightweight unread-count query. Accepts optional filter params for filtered counts. */
  unreadCount: (entityId: string, params?: FetchUnreadCountParams) =>
    ['notificationsUnreadCount', entityId, params ?? {}] as const,
};

interface UseNotificationsQueryOptions<TData = InfiniteData<NotificationsApiResponse>> {
  params?: FetchNotificationsParams;
  /**
   * Transform the raw InfiniteData before it reaches the component.
   * React Query applies structural sharing to the selected result,
   * returning a stable reference when the value hasn't changed.
   */
  select?: (data: InfiniteData<NotificationsApiResponse>) => TData;
}

/**
 * Fetch notifications with infinite scroll, optionally selecting a
 * derived slice of the data for structural sharing.
 *
 * @example
 * ```tsx
 * // Full infinite-query result (pages, fetchNextPage, etc.)
 * const query = useNotificationsQuery();
 *
 * // Flattened notification array with stable reference
 * const { data: notifications } = useNotificationsQuery({
 *   select: (data) => data.pages.flatMap((p) => p.items),
 * });
 *
 * // For unread count, use the dedicated useUnreadCount hook instead.
 * ```
 */
export function useNotificationsQuery<TData = InfiniteData<NotificationsApiResponse>>(
  options?: UseNotificationsQueryOptions<TData>
) {
  const { getAccessTokenSilently } = useAuth();
  const { activeEntity } = useEntity();
  const entityId = activeEntity?.id ?? 'default';
  const params = options?.params;

  return useSuspenseInfiniteQuery<
    NotificationsApiResponse,
    Error,
    TData,
    ReturnType<typeof notificationsKeys.infiniteList>,
    string | undefined
  >({
    queryKey: notificationsKeys.infiniteList(entityId, params ?? {}),
    queryFn: async ({ pageParam, signal }) => {
      const token = await getAccessTokenSilently();
      return fetchNotifications(token, entityId, { ...params, cursor: pageParam }, signal);
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: QUERY_STALE_TIME_MS,
    refetchInterval: POLLING_INTERVAL_MS,
    ...(options?.select ? { select: options.select } : {}),
  });
}
